// ── Carteira de contratos enviados (Clientes Ativos) ──────────────
// A tela do workspace Enterprise lista as empresas cujo contrato já saiu —
// enviado, assinado ou recusado — com o status do contrato e do boleto.
// "Envio pendente" fica de fora, e com ele a parceira, que não tem contrato.
//
// Uma empresa pode ter mais de um contrato no CS (o upsert só reaproveita o
// que ainda não tem venda). Vale o MAIS RECENTE, o mesmo critério da ficha.

import {
  empresaRecebeBoleto, statusBoleto, statusEnvioContrato,
  type CompetenciaStatus, type ContratoModalidade, type DocumentoStatus,
  type StatusBoleto, type StatusEnvioContrato,
} from './csModel';

/** Uma linha crua do repositório: o contrato com o que dá o status. */
export interface ContratoDaCarteira {
  contratoId: string;
  empresaId: string;
  workspaceId: string;
  /** Nome do cadastro; vazio quando a RLS não deixa ler a empresa. */
  empresaNome: string;
  razaoSocial: string;
  cnpj: string;
  modalidade: ContratoModalidade;
  createdAt: string;
  documentos: Array<{ status: DocumentoStatus }>;
  competencias: Array<{ competencia: string; status: CompetenciaStatus }>;
}

export interface LinhaCarteira {
  contratoId: string;
  empresaId: string;
  workspaceId: string;
  nome: string;
  cnpj: string;
  modalidade: ContratoModalidade;
  contrato: StatusEnvioContrato;
  /** null quando a empresa não recebe boleto (conveniada). */
  boleto: StatusBoleto | null;
}

export interface TotaisCarteira {
  empresas: number;
  aguardandoAssinatura: number;
  assinados: number;
  recusados: number;
  /** Boletos não gerados, emitidos sem pagamento ou atrasados. */
  boletosEmAberto: number;
  boletosAtrasados: number;
}

const comparavel = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

// "EntreVinhos - EVS EVENTOS LTDA": o nome do CRM (como o time chama) e a
// razão social do contrato (o que vai no documento). Iguais, aparece um só.
const nomeDaLinha = (empresaNome: string, razaoSocial: string): string => {
  const nome = empresaNome.trim();
  const razao = razaoSocial.trim();
  if (!nome || !razao) return nome || razao || 'Empresa sem nome';
  return comparavel(nome) === comparavel(razao) ? razao : `${nome} - ${razao}`;
};

export const montarCarteira =(itens: ContratoDaCarteira[]): { linhas: LinhaCarteira[]; totais: TotaisCarteira } => {
  const maisRecente = new Map<string, ContratoDaCarteira>();
  for (const item of itens) {
    const atual = maisRecente.get(item.empresaId);
    if (!atual || item.createdAt > atual.createdAt) maisRecente.set(item.empresaId, item);
  }

  const linhas: LinhaCarteira[] = [];
  for (const item of maisRecente.values()) {
    const contrato = statusEnvioContrato(item.documentos);
    if (contrato === 'envio_pendente') continue;
    linhas.push({
      contratoId: item.contratoId,
      empresaId: item.empresaId,
      workspaceId: item.workspaceId,
      nome: nomeDaLinha(item.empresaNome, item.razaoSocial),
      cnpj: item.cnpj,
      modalidade: item.modalidade,
      contrato,
      boleto: empresaRecebeBoleto(item.modalidade) ? statusBoleto(item.competencias) : null,
    });
  }
  linhas.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

  const totais: TotaisCarteira = {
    empresas: linhas.length,
    aguardandoAssinatura: linhas.filter(l => l.contrato === 'enviado').length,
    assinados: linhas.filter(l => l.contrato === 'assinado').length,
    recusados: linhas.filter(l => l.contrato === 'recusado').length,
    boletosEmAberto: linhas.filter(l => l.boleto !== null && l.boleto !== 'pago').length,
    boletosAtrasados: linhas.filter(l => l.boleto === 'atrasado').length,
  };
  return { linhas, totais };
};
